import type { BvNetworkRate, BvRateChargeUnit, BvRetailContractRate } from '../types/billValidationTypes';

// Master Data charge names — the single source of truth for what a charge is
// called across the expected invoice, the comparison table and the PDF report.
//
// Source: Master Data spreadsheet (Drive id
// `1K_V6x2SfYxG9uIsrDbV6Y-UM807Emd2cWfOGG8otXi0`), tab "New - BV Contract Rates",
// header rows. Job 39.
//
// These are the published names verbatim, grouped as the sheet groups them. They
// document the vocabulary and back the supply/metering name builders below. Only
// the retail contract tab is covered — distribution line names are out of scope.
export const CANONICAL_CHARGE_NAMES = {
  energy: [
    'Peak (¢/kWh)',
    'Shoulder (¢/kWh)',
    'Off-Peak (¢/kWh)',
    'Anytime (¢/kWh)',
    'Super-Peak (¢/kWh)',
  ],
  supply: [
    'Supply ($/servicePoint/day)',
    'Supply ($/servicePoint/month)',
    'Supply ($/servicePoint/year)',
  ],
  metering: [
    'Metering ($/meter/year)',
    'Metering ($/meter/day)',
    'Metering ($/servicePoint/year)',
    'Supplementary Metering ($/meter/year)',
  ],
  supplementaryServices: [
    'Supplementary Services ($/meter/year)',
    'Supplementary Services ($/meter/day)',
    'Supplementary Services ($/servicePoint/year)',
  ],
  greenSolar: [
    'GreenPower (¢/kWh)',
    'Feed-in Anytime (¢/kWh)',
    'Feed-in Peak (¢/kWh)',
    'Feed-in Off-Peak (¢/kWh)',
    'Feed-in Shoulder (¢/kWh)',
  ],
} as const;

// Bare emitted names for the retail energy lines (the expected invoice carries the
// name without the `(¢/kWh)` unit annotation — that lives on the volume/rate columns).
export const RETAIL_ENERGY_NAMES = {
  superPeak: 'Super Peak',
  peak: 'Peak',
  shoulder: 'Shoulder',
  offPeak: 'Off-Peak',
  // Feed-in names carry the `Solar ` prefix because that is the vocabulary the
  // BILLED side resolves to — chargeSectionRegistry's four retail entries, which
  // is what a charge map target like "Peak Buy back" canonicalises into. The
  // consumption bands above need no prefix because both sides already spell
  // those the same.
  //
  // CANONICAL_CHARGE_NAMES.greenSolar spells these WITHOUT the prefix
  // ('Feed-in Anytime (¢/kWh)'), but that list is the contract RATE sheet's
  // vocabulary — the names of rate columns, not of invoice lines. Emitting those
  // here keys the expected side as `feed in peak` against the invoice's
  // `solar feed in peak`, and comparisonRows renders one charge as two rows,
  // expected-only and invoice-only, with the variance wrong on both.
  feedInAnytime: 'Solar Feed-in Anytime',
  // Banded buy-back: a retailer that prices export by time of use prints one
  // line per window (AGL: "Peak Buy back" / "Shoulder Buy back" / "Off Peak
  // Buy back") rather than a single anytime total.
  feedInPeak: 'Solar Feed-in Peak',
  feedInShoulder: 'Solar Feed-in Shoulder',
  feedInOffPeak: 'Solar Feed-in Off-Peak',
} as const;

type ChargeHolder = 'servicePoint' | 'meter';
type ChargePeriod = 'day' | 'month' | 'year';

// Stored units are BvRateChargeUnit: per_{meter,servicepoint}_{day,month,year}.
// The holder token (meter vs servicePoint) and the time period both come off the
// stored unit string; an absent unit falls back to the historical assumption.
function chargeHolder(unit: string | undefined, fallback: ChargeHolder): ChargeHolder {
  if (!unit) return fallback;
  if (/meter/i.test(unit)) return 'meter';
  if (/servicepoint/i.test(unit)) return 'servicePoint';
  return fallback;
}

function chargePeriod(unit: string | undefined, fallback: ChargePeriod): ChargePeriod {
  if (!unit) return fallback;
  if (unit.endsWith('_year')) return 'year';
  if (unit.endsWith('_month')) return 'month';
  if (unit.endsWith('_day')) return 'day';
  return fallback;
}

// Job 60: what the rate is charged PER — the thing the QTY column has to count.
// A `$/meter/year` metering charge counts meters; a `$/servicePoint/day` supply
// charge counts service points. Exported because the shadow calculation has to
// pick the quantity that matches the unit it is about to print.
export function chargeHolderOf(
  unit: string | undefined,
  fallback: ChargeHolder = 'meter',
): ChargeHolder {
  return chargeHolder(unit, fallback);
}

const TIME_TOKENS = new Set(['day', 'days', 'month', 'months', 'year', 'years', 'annum', 'yr', 'mth']);

/**
 * The quantity a network price is charged over, read off the price type that
 * Distribution Definitions holds for the bucket (`bv_site_network.price_type`).
 *
 * `c/kWh` → kWh. `$/kVA/day` → kVA. `$/day` → days, because a purely time-based
 * price is charged over days and nothing else.
 *
 * Job 20 wants the network rows to state the unit the DEFINITION uses rather
 * than one inferred downstream, so this reads the definition's own string and
 * does not consult the usage row at all. Returns null when the price type is
 * absent or has no denominator — the caller then keeps whatever the pipeline
 * recorded, rather than replacing a real unit with a guess.
 */
export function quantityUnitForPriceType(priceType: string | null | undefined): string | null {
  if (!priceType) return null;
  const parts = priceType.split('/').map((p) => p.trim()).filter(Boolean);
  // The first part is the currency (c, $, cents) — everything after it describes
  // what is being counted.
  const denominators = parts.slice(1);
  if (denominators.length === 0) return null;

  const quantity = denominators.find((d) => !TIME_TOKENS.has(d.toLowerCase()));
  if (quantity) return quantity;

  // Every denominator was a time period, so days is the quantity.
  return 'days';
}

// `Supply ($/servicePoint/day|month|year)` — holder is servicePoint unless the
// stored unit is a per_meter_* variant, in which case it is meter.
export function supplyChargeName(unit: string | undefined): string {
  return `Supply ($/${chargeHolder(unit, 'servicePoint')}/${chargePeriod(unit, 'day')})`;
}

// `Metering ($/meter/day|month|year)` — same rule; an absent unit keeps the
// historical $/meter/year assumption that `metering_annual` was quoted annually.
export function meteringChargeName(unit: string | undefined): string {
  return perMeterChargeName('Metering', unit);
}

// The same rule for the contract's other per-meter charges:
// `Supplementary Metering ($/meter/year)`, `Supplementary Services ($/meter/day)`.
// The unit comes off for pairing (expectedVocabulary), leaving the vocabulary name.
export function perMeterChargeName(label: string, unit: string | undefined): string {
  return `${label} ($/${chargeHolder(unit, 'meter')}/${chargePeriod(unit, 'year')})`;
}

// Job 20: the rate unit on its own, for a line whose stored rate is the
// contracted figure rather than a converted daily one — `$/meter/year`,
// `$/servicePoint/month`. The comparison prints this beside the number so an
// expected $/meter/year and a billed $/meter/day cannot read as the same thing.
export function chargeRateUnit(
  unit: string | undefined,
  fallbackHolder: 'meter' | 'servicePoint' = 'meter',
  fallbackPeriod: 'day' | 'month' | 'year' = 'year',
): string {
  return `$/${chargeHolder(unit, fallbackHolder)}/${chargePeriod(unit, fallbackPeriod)}`;
}

// Old emitted names (pre-Job-39) mapped to their canonical replacement. Lets bills
// calculated before the rename, and charge-alias mappings saved against the old
// names, still pair and resolve after the rename. Keep in sync with the renames in
// shadowCalculationService.ts and the supply/metering builders above.
export const LEGACY_CHARGE_NAMES: Record<string, string> = {
  'Peak Energy': RETAIL_ENERGY_NAMES.peak,
  'Shoulder Energy': RETAIL_ENERGY_NAMES.shoulder,
  'Off-Peak Energy': RETAIL_ENERGY_NAMES.offPeak,
  'Feed-in Credit': RETAIL_ENERGY_NAMES.feedInAnytime,
  // Emitted un-prefixed until the feed-in lines were renamed to the billed
  // side's `Solar ` vocabulary. Bills calculated before that store the old name
  // in shadow_result, so without this they would keep pairing against nothing.
  'Feed-in Anytime': RETAIL_ENERGY_NAMES.feedInAnytime,
  'Supply Charge (/day)': 'Supply ($/servicePoint/day)',
  'Supply Charge (/month)': 'Supply ($/servicePoint/month)',
  'Supply Charge (/year)': 'Supply ($/servicePoint/year)',
  'Meter Data Service (/day)': 'Metering ($/meter/day)',
  'Meter Data Service (/month)': 'Metering ($/meter/month)',
  'Meter Data Service (/year)': 'Metering ($/meter/year)',
};

// Legacy names keyed by their normalised form so lookups tolerate case,
// surrounding whitespace, and hyphen/underscore/space differences. Retailers
// spell one charge both ways: SmartestEnergy prints "Off Peak Energy" where the
// list says "Off-Peak Energy" (NMI 62030050188), and without the fold its
// Off-Peak never paired while "Peak Energy" did. Same fold as comparisonRows'
// pairKey; a test checks no two legacy names collapse onto one key.
const legacyKey = (s: string): string => s.trim().toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');
const LEGACY_BY_NORM: Record<string, string> = Object.fromEntries(
  Object.entries(LEGACY_CHARGE_NAMES).map(([legacy, canonical]) => [legacyKey(legacy), canonical]),
);

// Returns the canonical Master Data name for a legacy description, or the input
// unchanged if it isn't a known legacy name. Matches case-insensitively, ignores
// surrounding whitespace, and treats hyphens, underscores and spaces alike; the
// input string itself is returned verbatim (untrimmed) when there is no match,
// so callers keep whatever they passed.
export function canonicalChargeName(description: string): string {
  return LEGACY_BY_NORM[legacyKey(description)] ?? description;
}

// ── Canonical section homes ───────────────────────────────────────────────────
// Some charges belong to one section no matter which section the calculation or
// the retailer's invoice filed them under. Supply is the case: it is read off the
// retail contract (`bv_contract_rate.supply`) but it is a standing charge for the
// connection, not energy, and `RATE_ROWS` below has always grouped it under
// "Metering Charges".
//
// This is the ONE definition of "that is the supply charge". It is used by the
// billed-side reclassify pass (`invoiceParserService.reclassifySupplyLines`) and
// by the Comparison table's row pass (`comparisonRows.applyCanonicalSections`).
// Two copies of this rule is how the same charge ended up in two sections.
//
// Anchored at the start on purpose, and matched against the CANONICAL row label
// as well as raw invoice wording:
//   matches  — 'Supply', 'Supply Charge', 'Supply Charge (/day)',
//              'Supply ($/servicePoint/day)', 'Supply ($/meter/day)'
//   excludes — 'Network Supply Charge' (a distribution charge; an unanchored
//              /supply/ would drag it into metering)
//   excludes — 'Service Charge' / 'Retail Service Charge' (stay in retail), and
//              'Supplementary Metering' / 'Supplementary Services' (different
//              charges — 'supply' is not a prefix of 'supplementary')
export function isSupplyCharge(description: string): boolean {
  return /^\s*supply\b/i.test(description);
}

// ── Rate-period display rows ──────────────────────────────────────────────────
// The "New - BV Contract Rates" tab carries two header rows: row 1 is the charge
// category (Retail Charges / Metering Charges / Environmental Charge / Solar
// Charges) and row 2 the charge name. The rate tables in the site UI and in the
// compliance report both render from the list below, in sheet column order
// (M → AF), so the vocabulary can only ever drift in one place. Job 39.
export const CHARGE_UNIT_LABELS: Record<BvRateChargeUnit, string> = {
  per_meter_day: '$/meter/day',
  per_meter_month: '$/meter/month',
  per_meter_year: '$/meter/year',
  per_servicepoint_day: '$/servicePoint/day',
  per_servicepoint_month: '$/servicePoint/month',
  per_servicepoint_year: '$/servicePoint/year',
};

export const CHARGE_UNIT_OPTIONS: { value: BvRateChargeUnit; label: string }[] =
  (Object.keys(CHARGE_UNIT_LABELS) as BvRateChargeUnit[]).map((value) => ({
    value,
    label: CHARGE_UNIT_LABELS[value],
  }));

// `12.2596¢/kWh` — the sheet quotes energy, feed-in and GreenPower rates in cents.
export function formatKwhRate(value: number | undefined): string {
  return value != null ? `${value}¢/kWh` : '—';
}

// `$50.63/servicePoint/month` — a dollar charge with the unit it is quoted in. A
// charge stored without a unit renders bare rather than guessing a denominator.
export function formatChargeAmount(
  value: number | undefined,
  unit: BvRateChargeUnit | undefined,
): string {
  if (value == null) return '—';
  return unit ? `$${value}${CHARGE_UNIT_LABELS[unit].slice(1)}` : `$${value}`;
}

type KwhField = 'peak_rate' | 'shoulder_rate' | 'off_peak_rate' | 'anytime_rate'
  | 'super_peak_rate' | 'green_power_rate' | 'feed_in_rate_anytime'
  | 'feed_in_rate_peak' | 'feed_in_rate_off_peak' | 'feed_in_rate_shoulder';

type ChargeField = 'supply_charge' | 'supplementary_metering_rate'
  | 'metering_rate' | 'supplementary_services_rate';

type UnitField = 'supply_unit' | 'supplementary_metering_unit'
  | 'metering_unit' | 'supplementary_services_unit';

export type RateRow =
  | { group: string; label: string; kind: 'kwh'; amount: KwhField }
  | { group: string; label: string; kind: 'charge'; amount: ChargeField; unit: UnitField };

export const RATE_ROWS: RateRow[] = [
  // Columns M–Q
  { group: 'Retail Charges',      label: 'Peak',                    kind: 'kwh',    amount: 'peak_rate' },
  { group: 'Retail Charges',      label: 'Shoulder',                kind: 'kwh',    amount: 'shoulder_rate' },
  { group: 'Retail Charges',      label: 'Off-Peak',                kind: 'kwh',    amount: 'off_peak_rate' },
  { group: 'Retail Charges',      label: 'Anytime',                 kind: 'kwh',    amount: 'anytime_rate' },
  { group: 'Retail Charges',      label: 'Super-Peak',              kind: 'kwh',    amount: 'super_peak_rate' },
  // Columns R–AA
  { group: 'Metering Charges',    label: 'Supply',                  kind: 'charge', amount: 'supply_charge',                 unit: 'supply_unit' },
  { group: 'Metering Charges',    label: 'Supplementary Metering',  kind: 'charge', amount: 'supplementary_metering_rate',   unit: 'supplementary_metering_unit' },
  { group: 'Metering Charges',    label: 'Metering',                kind: 'charge', amount: 'metering_rate',                 unit: 'metering_unit' },
  { group: 'Metering Charges',    label: 'Supplementary Services',  kind: 'charge', amount: 'supplementary_services_rate',   unit: 'supplementary_services_unit' },
  // Column AB
  { group: 'Environmental Charge', label: 'GreenPower',             kind: 'kwh',    amount: 'green_power_rate' },
  // Columns AC–AF
  { group: 'Solar Charges',       label: 'Feed-in Anytime',         kind: 'kwh',    amount: 'feed_in_rate_anytime' },
  { group: 'Solar Charges',       label: 'Feed-in Peak',            kind: 'kwh',    amount: 'feed_in_rate_peak' },
  { group: 'Solar Charges',       label: 'Feed-in Off-Peak',        kind: 'kwh',    amount: 'feed_in_rate_off_peak' },
  { group: 'Solar Charges',       label: 'Feed-in Shoulder',        kind: 'kwh',    amount: 'feed_in_rate_shoulder' },
];

// Renders one rate row's cell for a stored rate period.
export function rateRowValue(row: RateRow, rate: BvRetailContractRate): string {
  return row.kind === 'kwh'
    ? formatKwhRate(rate[row.amount])
    : formatChargeAmount(rate[row.amount], rate[row.unit]);
}

// ── Network tariff display rows (job 35) ──────────────────────────────────────
// The network card renders from this list the way the retail card renders from
// RATE_ROWS above, so the two cards on a site page are the same object with
// different rows rather than two different tables.
//
// Deliberately a FIXED set: a network tariff's catalog buckets vary by
// distributor, and a card whose rows changed shape per code could not be read
// down a column. Buckets outside this set stay in the read-only catalog view —
// they are not charges the fallback calculation prices.

type NetworkKwhField = 'peak_rate' | 'shoulder_rate' | 'off_peak_rate' | 'anytime_rate';
type NetworkDemandField = 'peak_demand_rate' | 'anytime_demand_rate';
type NetworkQuantityField = 'capacity_kw' | 'demand_kva';

export type NetworkRateRow =
  | { group: string; label: string; kind: 'kwh'; amount: NetworkKwhField }
  // $/kVA/year — the unit network demand is quoted in. Held apart from the
  // retail `demand` column, which is c/kWh/day.
  | { group: string; label: string; kind: 'demand'; amount: NetworkDemandField }
  // A contracted quantity, not a price: kW or kVA agreed with the distributor.
  | { group: string; label: string; kind: 'quantity'; amount: NetworkQuantityField; unit: 'kW' | 'kVA' }
  | { group: string; label: string; kind: 'charge'; amount: 'supply_charge'; unit: 'supply_unit' };

export const NETWORK_RATE_ROWS: NetworkRateRow[] = [
  { group: 'Network Usage',  label: 'Peak',            kind: 'kwh',      amount: 'peak_rate' },
  { group: 'Network Usage',  label: 'Shoulder',        kind: 'kwh',      amount: 'shoulder_rate' },
  { group: 'Network Usage',  label: 'Off-Peak',        kind: 'kwh',      amount: 'off_peak_rate' },
  { group: 'Network Usage',  label: 'Anytime',         kind: 'kwh',      amount: 'anytime_rate' },
  { group: 'Demand',         label: 'Peak Demand',     kind: 'demand',   amount: 'peak_demand_rate' },
  { group: 'Demand',         label: 'Anytime Demand',  kind: 'demand',   amount: 'anytime_demand_rate' },
  { group: 'Demand',         label: 'Capacity',        kind: 'quantity', amount: 'capacity_kw', unit: 'kW' },
  { group: 'Demand',         label: 'Contracted Demand', kind: 'quantity', amount: 'demand_kva', unit: 'kVA' },
  { group: 'Network Supply', label: 'Standing Charge', kind: 'charge',   amount: 'supply_charge', unit: 'supply_unit' },
];

/** `$142/kVA/year`. Demand is always quoted annually here — see migration 101. */
export function formatDemandRate(value: number | undefined): string {
  return value != null ? `$${value}/kVA/year` : '—';
}

/** `400 kW`. A quantity keeps its unit beside it so it cannot read as a rate. */
export function formatQuantity(value: number | undefined, unit: 'kW' | 'kVA'): string {
  return value != null ? `${value.toLocaleString('en-AU')} ${unit}` : '—';
}

// Renders one network row's cell. An em dash for an unset charge, never a bare
// 0 — a tariff with no shoulder rate is not one that charges nothing for it.
export function networkRateRowValue(row: NetworkRateRow, rate: BvNetworkRate): string {
  switch (row.kind) {
    case 'kwh':      return formatKwhRate(rate[row.amount]);
    case 'demand':   return formatDemandRate(rate[row.amount]);
    case 'quantity': return formatQuantity(rate[row.amount], row.unit);
    case 'charge':   return formatChargeAmount(rate[row.amount], rate[row.unit]);
  }
}
